import { otherMixedTitles } from '@digital-twins/shared-types'

/** The rollup labels the analysis stamps (quick_study/analysis.py `_OTHER_THEME_LABEL`,
 *  `_MONADIC_OTHER_LABEL`), plus the localized residual titles of the shared wording pack
 *  ("Otros / mixto"), which results stored before `is_other` carry. Read only for stored
 *  results that predate `is_other`. */
const STORED_OTHER_ROLLUP_LABELS = new Set(['Other', 'Other / mixed', ...otherMixedTitles()])

/** What the Other check reads off a theme: the typed flag, and the title for the legacy reader. */
export interface OtherFlaggedTheme {
  is_other?: boolean | null
  theme?: string | null
}

// The backend rolls tiny (suppressed) themes into an "Other" bucket. It renders
// muted, always LAST, and never carries a sentiment chip (a roll-up has no single
// sentiment). Read from the typed `is_other` flag the analysis stamps on its rollups
// (plots plan S), never from the title: a theme the model titled "Other brands" is a
// finding. Results stored before the flag keep a legacy reader: an exact match on the
// stamped rollup labels, never a prefix match.
// Owned here (the leaf module) and re-exported from QuickStudyOpenEndCard for its
// existing importers, so the theme-grouping utils have no import cycle.
export function isOtherTheme(theme: OtherFlaggedTheme | null | undefined): boolean {
  if (typeof theme?.is_other === 'boolean') {
    return theme.is_other
  }
  // legacy: stored results without the flag.
  return STORED_OTHER_ROLLUP_LABELS.has(String(theme?.theme ?? '').trim())
}

/** The printed name of a theme: the rollup reads `otherLabel` (the viewer's language), a
 *  real theme its own title. */
export function themeDisplayName(theme: OtherFlaggedTheme & { theme: string }, otherLabel: string): string {
  return isOtherTheme(theme) ? otherLabel : theme.theme
}

/** Stable re-order that only moves "Other" roll-up bucket(s) to the end. */
export function orderThemesOtherLast<T extends OtherFlaggedTheme & { theme: string }>(themes: Array<T>): Array<T> {
  return [...themes].sort((a, b) => Number(isOtherTheme(a)) - Number(isOtherTheme(b)))
}

/** FE-only synthetic plot ids for the monadic OE theme-view switch (Heatmap ⇄ Bars). The
 *  backend never lists them in allowed_plots — they are always-valid VIEW choices for the
 *  themes exhibit, so reportPlotFor must pass them through. */
export const THEME_VIEW_PLOT_IDS = ['themes_heatmap', 'themes_bars'] as const

export function isThemeViewPlotId(plot: string | undefined | null): boolean {
  return (THEME_VIEW_PLOT_IDS as readonly string[]).includes(String(plot ?? ''))
}

export type ThemeSentimentGroup = 'positive' | 'mixed' | 'negative'

/** Minimal shape the grouping reads — generic so BOTH the monadic shared-codeframe themes
 *  (percent lives on each variant) and the single-list open-end themes (percent on the theme)
 *  use ONE util. */
export interface GroupableTheme {
  theme: string
  /** The analysis's rollup flag (plots plan S); absent on stored results. */
  is_other?: boolean
  sentiment?: string
  percent?: number
  variants?: Array<{ percent?: number }>
}

export interface SentimentGroupedThemes<T> {
  group: ThemeSentimentGroup
  themes: Array<T>
}

const GROUP_ORDER: Array<ThemeSentimentGroup> = ['positive', 'mixed', 'negative']

function maxPercent(theme: GroupableTheme): number {
  if (theme.variants && theme.variants.length > 0) {
    return Math.max(...theme.variants.map((v) => v.percent ?? 0))
  }
  return theme.percent ?? 0
}

function sentimentGroupOf(theme: GroupableTheme): ThemeSentimentGroup {
  const key = String(theme.sentiment ?? '').trim().toLowerCase()
  if (key === 'positive') return 'positive'
  if (key === 'negative') return 'negative'
  return 'mixed'
}

/** Group non-"Other" themes into Positive → Mixed → Negative sections, dropping empty groups,
 *  and return the "Other" roll-up bucket(s) separately so callers append them LAST (chip-free).
 *  Sentiment bucketing: 'positive' → positive; 'negative' → negative; everything else
 *  ('mixed', 'neutral', missing, unknown) → mixed. Within a group, sort by the theme's MAX
 *  percent across variants (or its own `percent`) descending. Stable, pure. */
export function groupThemesBySentiment<T extends GroupableTheme>(
  themes: Array<T>,
): { groups: Array<SentimentGroupedThemes<T>>; other: Array<T> } {
  const other: Array<T> = []
  const buckets: Record<ThemeSentimentGroup, Array<T>> = {
    positive: [],
    mixed: [],
    negative: [],
  }

  for (const theme of themes) {
    if (isOtherTheme(theme)) {
      other.push(theme)
      continue
    }
    buckets[sentimentGroupOf(theme)].push(theme)
  }

  const indexed = new Map<T, number>()
  themes.forEach((theme, index) => indexed.set(theme, index))

  const groups: Array<SentimentGroupedThemes<T>> = []
  for (const group of GROUP_ORDER) {
    const bucket = buckets[group]
    if (bucket.length === 0) continue
    const sorted = [...bucket].sort((a, b) => {
      const diff = maxPercent(b) - maxPercent(a)
      if (diff !== 0) return diff
      return (indexed.get(a) ?? 0) - (indexed.get(b) ?? 0)
    })
    groups.push({ group, themes: sorted })
  }

  return { groups, other }
}

// ---------------------------------------------------------------------------
// Combo quant+OE band splits → MonadicThemeHeatmap adapter
// ---------------------------------------------------------------------------

/** Structural shape of a `theme_splits` band's raw theme entry, as spec'd by the backend
 *  contract (built in parallel — code against this, not the python source). */
export interface BandThemeSplitEntry {
  theme: string
  is_other?: boolean
  description?: string
  sentiment?: string
  percent?: number
  count?: number
}

/** Structural shape of one `theme_splits` band. */
export interface BandThemeSplitBand {
  id: string
  label?: string
  definition?: string
  n?: number
  themes?: Array<BandThemeSplitEntry>
}

/** Structural shape of the `theme_splits` result field this adapter reads. Kept local (this
 *  is the zero-import leaf module) rather than imported from QuickStudyPage.tsx. */
export interface BandThemeSplits {
  mode: string
  shared_codeframe?: boolean
  bands?: Array<BandThemeSplitBand>
}

export interface BandHeatmapVariant {
  creative_id: string
  label: string
  count: number
  percent: number
}

export interface BandHeatmapTheme {
  theme: string
  is_other?: boolean
  description?: string
  sentiment?: string
  variants: Array<BandHeatmapVariant>
}

export interface BandHeatmapColumn {
  id: string
  label: string
  /** Band definition + base, surfaced as the column header tooltip ("scores 1-2 of 5 · n=42"). */
  title?: string
}

export interface BandHeatmapData {
  themes: Array<BandHeatmapTheme>
  columns: Array<BandHeatmapColumn>
  /** Above-floor EXCLUDED never happens; these are the sub-floor bands (0<n<floor) left out of
   *  the grid — the caller renders an honesty note naming them. */
  excluded: Array<{ label: string; n: number }>
}

/** Adapts combo quant+OE `theme_splits` band data into the shape MonadicThemeHeatmap consumes.
 *  Returns null unless the backend has stamped a shared codeframe AND at least 2 bands actually
 *  carry themes — a single themed band (or an unaligned per-band codeframe) has nothing to
 *  compare, so the caller keeps its existing per-band fallback rendering instead. Pure, no
 *  rounding beyond passthrough. */
export function bandThemesToHeatmap(
  themeSplits: BandThemeSplits | null | undefined,
): BandHeatmapData | null {
  if (!themeSplits?.shared_codeframe) return null

  const bands = themeSplits.bands ?? []
  const themedBands = bands.filter((band) => (band.themes ?? []).length > 0)
  if (themedBands.length < 2) return null

  const columns: Array<BandHeatmapColumn> = themedBands.map((band) => ({
    id: band.id,
    label: band.label ?? band.id,
    title: [band.definition, band.n != null ? `n=${band.n}` : ''].filter(Boolean).join(' · ') || undefined,
  }))

  const themeOrder: Array<string> = []
  const themeByKey = new Map<string, BandHeatmapTheme>()
  for (const band of themedBands) {
    const label = band.label ?? band.id
    for (const theme of band.themes ?? []) {
      let entry = themeByKey.get(theme.theme)
      if (!entry) {
        entry = { theme: theme.theme, description: theme.description, sentiment: theme.sentiment, variants: [] }
        if (typeof theme.is_other === 'boolean') {
          entry.is_other = theme.is_other
        }
        themeByKey.set(theme.theme, entry)
        themeOrder.push(theme.theme)
      }
      // Defensive: the backend keys buckets by id and PROMPTS for distinct labels, but a
      // duplicate label inside one band would otherwise add a second same-band variant that
      // the heatmap cell's `.find` silently drops (an undercount). Sum into the existing
      // variant instead so a label collision can only ever over-group, never lose counts.
      const existing = entry.variants.find((v) => v.creative_id === band.id)
      if (existing) {
        existing.count += theme.count ?? 0
        existing.percent += theme.percent ?? 0
      } else {
        entry.variants.push({
          creative_id: band.id,
          label,
          count: theme.count ?? 0,
          percent: theme.percent ?? 0,
        })
      }
    }
  }
  const themes = themeOrder.map((key) => themeByKey.get(key) as BandHeatmapTheme)

  const excluded = bands
    .filter((band) => (band.n ?? 0) > 0 && (band.themes ?? []).length === 0)
    .map((band) => ({ label: band.label ?? band.id, n: band.n ?? 0 }))

  return { themes, columns, excluded }
}
